import { useState, useEffect } from 'react';
import { Alert, Modal, Form, Button, Accordion } from 'react-bootstrap';
import LoadingButton from 'components/LoadingButton/LoadingButton';
import Attachments from 'components/Attachments/Attachments';
import { GET_PROMO_UPDATES_FOR_ITEM, useLazyQuery } from 'utils/graphql';
import { useTranslation } from 'react-i18next';

const HISTORY_PAGE_SIZE = 6;

const PromoItemModal = ({
    show,
    onHide,
    mode, // 'create' | 'detail'
    item, // existing promo item for detail, null for create
    onSubmit,
    isSubmitting,
    existingReasons,
    savedUpdateId, // set by parent after a successful update -- triggers the post-save view
}) => {
    const { t } = useTranslation();

    const formatDate = (d) => (d ? new Date(d).toISOString().slice(0, 10) : '\u2014');
    const formatDateTime = (d) => {
        if (!d) return '\u2014';
        const dt = new Date(d);
        return `${dt.toISOString().slice(0, 10)} ${dt.toISOString().slice(11, 16)}`;
    };

    // Detail sub-view: 'detail' (default) | 'update' (new update form) | 'post-save' (attach files)
    const [detailView, setDetailView] = useState('detail');

    // Create form state
    const [reason, setReason] = useState('');
    const [description, setDescription] = useState('');
    const [brand, setBrand] = useState('');
    const [quantity, setQuantity] = useState('');
    const [eventDate, setEventDate] = useState('');
    const [distributionMethod, setDistributionMethod] = useState('');
    const [notes, setNotes] = useState('');

    // Update form state
    const [updateInventory, setUpdateInventory] = useState('');
    const [updateRemainingUse, setUpdateRemainingUse] = useState('');
    const [updateNotes, setUpdateNotes] = useState('');

    const [validated, setValidated] = useState(false);
    const [formErrors, setFormErrors] = useState({});
    const [saveSuccess, setSaveSuccess] = useState(false);

    // Update logs
    const [updateLogs, setUpdateLogs] = useState([]);
    const [isLoadingLogs, setIsLoadingLogs] = useState(false);
    const [historyPage, setHistoryPage] = useState(1);

    const [getPromoUpdates] = useLazyQuery(GET_PROMO_UPDATES_FOR_ITEM, {
        fetchPolicy: 'network-only',
    });

    // Load update logs when opening detail mode
    const loadUpdateLogs = async () => {
        if (!item?.id) return;
        setIsLoadingLogs(true);
        try {
            const { data: updatesData } = await getPromoUpdates({
                variables: { promo_item_id: item.id },
            });
            const logs = (updatesData?.table?.records || []).map((r) => {
                const entry = r.gc_inventory_promo_item_updates;
                const updatedByName =
                    entry.updated_by_propertyuser__c_list?.[0]?.propertyuser__c?.name ||
                    t('deleted_user');
                return {
                    id: entry.id,
                    date: entry.updated_date,
                    updatedByName,
                    new_remaining_inventory: entry.new_remaining_inventory,
                    remaining_use: entry.remaining_use,
                    notes: entry.notes,
                };
            });
            setUpdateLogs(logs);
            setHistoryPage(1);
        } catch {
            // non-critical
        }
        setIsLoadingLogs(false);
    };

    useEffect(() => {
        if (show && mode === 'detail' && item?.id) {
            loadUpdateLogs();
        }
    }, [show, mode, item?.id]);

    // On save: reload logs, show success banner, return to detail view
    useEffect(() => {
        if (savedUpdateId && mode === 'detail') {
            setSaveSuccess(true);
            setDetailView('detail');
            setHistoryPage(1);
            loadUpdateLogs();
        }
    }, [savedUpdateId]);

    useEffect(() => {
        if (show) {
            if (mode === 'create') {
                resetCreateForm();
            }
            if (mode === 'detail') {
                setHistoryPage(1);
            }
            setDetailView('detail');
            resetUpdateForm();
            setSaveSuccess(false);
            setValidated(false);
            setFormErrors({});
        }
    }, [show, mode]);

    const resetCreateForm = () => {
        setReason('');
        setDescription('');
        setBrand('');
        setQuantity('');
        setEventDate('');
        setDistributionMethod('');
        setNotes('');
    };

    const resetUpdateForm = () => {
        setUpdateInventory('');
        setUpdateRemainingUse('');
        setUpdateNotes('');
        setFormErrors({});
        setValidated(false);
    };

    const validateCreate = () => {
        const errors = {};
        if (!reason) errors.reason = 'reason_for_purchase_empty_error';
        if (!description) errors.description = 'description_empty_error';
        if (!brand) errors.brand = 'brand_empty_error';
        if (quantity === '') errors.quantity = 'quantity_empty_error';
        if (!eventDate) errors.eventDate = 'event_date_empty_error';
        if (!distributionMethod) errors.distributionMethod = 'distribution_method_empty_error';
        setFormErrors(errors);
        setValidated(true);
        return Object.keys(errors).length === 0;
    };

    const handleCreateSubmit = () => {
        if (!validateCreate()) return;
        onSubmit({
            reason: reason || null,
            description: description || null,
            brand: brand || null,
            quantity: quantity !== '' ? parseInt(quantity) : null,
            event_date: eventDate || null,
            distribution_method: distributionMethod || null,
            notes: notes || null,
        });
    };

    const handleUpdateSubmit = () => {
        const errors = {};
        if (updateInventory === '') errors.updateInventory = 'remaining_inventory_empty_error';
        if (!updateRemainingUse) errors.updateRemainingUse = 'remaining_use_empty_error';
        if (Object.keys(errors).length > 0) {
            setFormErrors(errors);
            setValidated(true);
            return;
        }
        onSubmit({
            _isUpdate: true,
            new_remaining_inventory: updateInventory !== '' ? parseInt(updateInventory) : null,
            remaining_use: updateRemainingUse || null,
            notes: updateNotes || null,
        });
    };

    const reasonOptions = existingReasons || [];

    // -- Detail mode: new update form --
    if (mode === 'detail' && detailView === 'update') {
        return (
            <Modal show={show} onHide={onHide} size="lg">
                <Modal.Header closeButton>
                    <Modal.Title>
                        <h5>{t('new_update_log')}</h5>
                    </Modal.Title>
                </Modal.Header>
                <Modal.Body>
                    <Form>
                        <Form.Group className="mb-3">
                            <Form.Label>
                                <span className="required-field">*</span> {t('remaining_inventory')}
                            </Form.Label>
                            <Form.Control
                                type="number"
                                min="0"
                                isInvalid={validated && !!formErrors.updateInventory}
                                value={updateInventory}
                                onChange={(e) => setUpdateInventory(e.target.value)}
                            />
                            {formErrors.updateInventory && (
                                <Form.Control.Feedback type="invalid">
                                    {t(formErrors.updateInventory)}
                                </Form.Control.Feedback>
                            )}
                            <Form.Text className="text-muted small">
                                {t('update_inventory_help')}
                            </Form.Text>
                        </Form.Group>

                        <Form.Group className="mb-3">
                            <Form.Label>
                                <span className="required-field">*</span> {t('remaining_use')}
                            </Form.Label>
                            <Form.Control
                                type="text"
                                isInvalid={validated && !!formErrors.updateRemainingUse}
                                value={updateRemainingUse}
                                onChange={(e) => setUpdateRemainingUse(e.target.value)}
                            />
                            {formErrors.updateRemainingUse && (
                                <Form.Control.Feedback type="invalid">
                                    {t(formErrors.updateRemainingUse)}
                                </Form.Control.Feedback>
                            )}
                            <Form.Text className="text-muted small">
                                {t('remaining_use_description')}
                            </Form.Text>
                        </Form.Group>

                        <Form.Group className="mb-3">
                            <Form.Label>{t('notes')}</Form.Label>
                            <Form.Control
                                as="textarea"
                                rows={3}
                                value={updateNotes}
                                onChange={(e) => setUpdateNotes(e.target.value)}
                            />
                        </Form.Group>
                    </Form>
                </Modal.Body>
                <Modal.Footer>
                    <LoadingButton onClick={handleUpdateSubmit} isLoading={isSubmitting}>
                        {t('save')}
                    </LoadingButton>
                    <Button
                        variant="secondary"
                        onClick={() => {
                            resetUpdateForm();
                            setDetailView('detail');
                        }}
                    >
                        {t('cancel')}
                    </Button>
                </Modal.Footer>
            </Modal>
        );
    }

    // -- Detail mode: full item details + update log timeline --
    if (mode === 'detail' && item) {
        const latestRemaining =
            updateLogs.length > 0 && updateLogs[0].new_remaining_inventory != null
                ? updateLogs[0].new_remaining_inventory
                : item.remaining_inventory != null
                  ? item.remaining_inventory
                  : item.quantity;
        const totalHistoryPages = Math.max(1, Math.ceil(updateLogs.length / HISTORY_PAGE_SIZE));
        const historyStart = (historyPage - 1) * HISTORY_PAGE_SIZE;
        const paginatedLogs = updateLogs.slice(historyStart, historyStart + HISTORY_PAGE_SIZE);
        return (
            <Modal show={show} onHide={onHide} size="lg">
                <Modal.Header closeButton>
                    <Modal.Title>
                        <h5>{t('promo_item_details')}</h5>
                    </Modal.Title>
                </Modal.Header>
                <Modal.Body>
                    {saveSuccess && (
                        <Alert
                            variant="success"
                            dismissible
                            onClose={() => setSaveSuccess(false)}
                            className="mb-3"
                        >
                            {t('update_saved')}
                        </Alert>
                    )}
                    {/* Promo item summary */}
                    <div className="card-summary mb-3 p-3 bg-light rounded">
                        <div className="row">
                            <div className="col-md-6">
                                <small className="text-muted">{t('description')}</small>
                                <div>{item.description || '\u2014'}</div>
                            </div>
                            <div className="col-md-6">
                                <small className="text-muted">{t('brand')}</small>
                                <div>{item.brand || '\u2014'}</div>
                            </div>
                        </div>
                        <div className="row mt-2">
                            <div className="col-md-6">
                                <small className="text-muted">{t('quantity')}</small>
                                <div>{item.quantity != null ? item.quantity : '\u2014'}</div>
                            </div>
                            <div className="col-md-6">
                                <small className="text-muted">{t('remaining_inventory')}</small>
                                <div>
                                    {latestRemaining != null ? Number(latestRemaining) : '\u2014'}
                                </div>
                            </div>
                        </div>
                        <div className="row mt-2">
                            <div className="col-md-6">
                                <small className="text-muted">{t('event_date')}</small>
                                <div>{formatDate(item.event_date)}</div>
                            </div>
                            <div className="col-md-6">
                                <small className="text-muted">{t('distribution_method')}</small>
                                <div>{item.distribution_method || '\u2014'}</div>
                            </div>
                        </div>
                        {item.reason && (
                            <div className="row mt-2">
                                <div className="col-md-6">
                                    <small className="text-muted">{t('reason')}</small>
                                    <div>{item.reason}</div>
                                </div>
                            </div>
                        )}
                        {item.remaining_use && (
                            <div className="row mt-2">
                                <div className="col-md-6">
                                    <small className="text-muted">{t('remaining_use')}</small>
                                    <div>{item.remaining_use}</div>
                                </div>
                            </div>
                        )}
                        {item.notes && (
                            <div className="row mt-2">
                                <div className="col-12">
                                    <small className="text-muted">{t('notes')}</small>
                                    <div>{item.notes}</div>
                                </div>
                            </div>
                        )}
                    </div>

                    {/* New Update button */}
                    <Button
                        className="w-100 mb-3"
                        onClick={() => {
                            setSaveSuccess(false);
                            resetUpdateForm();
                            setUpdateInventory(
                                latestRemaining != null ? String(latestRemaining) : ''
                            );
                            setUpdateRemainingUse(item.remaining_use || '');
                            setDetailView('update');
                        }}
                    >
                        {t('new_update_log')}
                    </Button>

                    {/* Update log timeline */}
                    <h6 className="mb-2">{t('update_history')}</h6>
                    {isLoadingLogs && (
                        <div className="text-center my-3">
                            <div className="spinner-border spinner-border-sm" role="status" />
                        </div>
                    )}
                    {!isLoadingLogs && updateLogs.length === 0 && (
                        <p className="text-muted">{t('no_history')}</p>
                    )}
                    <Accordion>
                        {paginatedLogs.map((log) => (
                            <Accordion.Item eventKey={`log-${log.id}`} key={log.id}>
                                <Accordion.Header>
                                    <div>
                                        <strong>{t('inventory_update')}</strong>
                                        <span className="text-muted small ms-2">
                                            {formatDateTime(log.date)}
                                        </span>
                                        <span className="text-muted small ms-2">
                                            {t('by')} {log.updatedByName}
                                        </span>
                                    </div>
                                </Accordion.Header>
                                <Accordion.Body>
                                    <div className="row mb-2">
                                        <div className="col-md-6">
                                            <small className="text-muted">
                                                {t('remaining_inventory')}
                                            </small>
                                            <div>
                                                {log.new_remaining_inventory != null
                                                    ? log.new_remaining_inventory
                                                    : '\u2014'}
                                            </div>
                                        </div>
                                        {log.remaining_use != null && (
                                            <div className="col-md-6">
                                                <small className="text-muted">
                                                    {t('remaining_use')}
                                                </small>
                                                <div>{log.remaining_use}</div>
                                            </div>
                                        )}
                                    </div>
                                    {log.notes && (
                                        <div className="mb-2">
                                            <small className="text-muted">{t('notes')}</small>
                                            <div>{log.notes}</div>
                                        </div>
                                    )}
                                    <Attachments
                                        parentId={log.id}
                                        parentTable="gc_inventory_promo_item_updates"
                                        readOnly={false}
                                    />
                                </Accordion.Body>
                            </Accordion.Item>
                        ))}
                    </Accordion>
                    {!isLoadingLogs && updateLogs.length > HISTORY_PAGE_SIZE && (
                        <div className="d-flex justify-content-between align-items-center mt-3">
                            <Button
                                variant="outline-secondary"
                                size="sm"
                                disabled={historyPage <= 1}
                                onClick={() => setHistoryPage((p) => Math.max(1, p - 1))}
                            >
                                {'<'}
                            </Button>
                            <small className="text-muted">
                                {historyPage} / {totalHistoryPages}
                            </small>
                            <Button
                                variant="outline-secondary"
                                size="sm"
                                disabled={historyPage >= totalHistoryPages}
                                onClick={() =>
                                    setHistoryPage((p) => Math.min(totalHistoryPages, p + 1))
                                }
                            >
                                {'>'}
                            </Button>
                        </div>
                    )}
                </Modal.Body>
                <Modal.Footer>
                    <Button variant="secondary" onClick={onHide}>
                        {t('close')}
                    </Button>
                </Modal.Footer>
            </Modal>
        );
    }

    // -- Create mode --
    return (
        <Modal show={show} onHide={onHide} size="lg">
            <Modal.Header closeButton>
                <Modal.Title>
                    <h5>{t('create_promo_item')}</h5>
                </Modal.Title>
            </Modal.Header>
            <Modal.Body>
                <Form>
                    <Form.Group className="mb-3">
                        <Form.Label>
                            <span className="required-field">*</span> {t('reason_for_purchase')}
                        </Form.Label>
                        <Form.Control
                            type="text"
                            list="promo-reason-suggestions"
                            isInvalid={validated && !!formErrors.reason}
                            value={reason}
                            onChange={(e) => setReason(e.target.value)}
                            placeholder={t('enter_custom_reason')}
                        />
                        <datalist id="promo-reason-suggestions">
                            {reasonOptions.map((r, i) => (
                                <option key={i} value={r} />
                            ))}
                        </datalist>
                        {formErrors.reason && (
                            <Form.Control.Feedback type="invalid">
                                {t(formErrors.reason)}
                            </Form.Control.Feedback>
                        )}
                        <Form.Text className="text-muted small">
                            {t('reason_for_purchase_help')}
                        </Form.Text>
                    </Form.Group>

                    <Form.Group className="mb-3">
                        <Form.Label>
                            <span className="required-field">*</span> {t('description')}
                        </Form.Label>
                        <Form.Control
                            isInvalid={validated && formErrors.description}
                            type="text"
                            value={description}
                            onChange={(e) => setDescription(e.target.value)}
                        />
                        {formErrors.description && (
                            <Form.Control.Feedback type="invalid">
                                {t(formErrors.description)}
                            </Form.Control.Feedback>
                        )}
                        <Form.Text className="text-muted small">{t('description_help')}</Form.Text>
                    </Form.Group>

                    <div className="row">
                        <div className="col-md-6">
                            <Form.Group className="mb-3">
                                <Form.Label>
                                    <span className="required-field">*</span> {t('brand')}
                                </Form.Label>
                                <Form.Control
                                    type="text"
                                    isInvalid={validated && !!formErrors.brand}
                                    value={brand}
                                    onChange={(e) => setBrand(e.target.value)}
                                />
                                {formErrors.brand && (
                                    <Form.Control.Feedback type="invalid">
                                        {t(formErrors.brand)}
                                    </Form.Control.Feedback>
                                )}
                                <Form.Text className="text-muted small">
                                    {t('brand_help')}
                                </Form.Text>
                            </Form.Group>
                        </div>
                        <div className="col-md-6">
                            <Form.Group className="mb-3">
                                <Form.Label>
                                    <span className="required-field">*</span> {t('quantity')}
                                </Form.Label>
                                <Form.Control
                                    type="number"
                                    min="0"
                                    isInvalid={validated && !!formErrors.quantity}
                                    value={quantity}
                                    onChange={(e) => setQuantity(e.target.value)}
                                />
                                {formErrors.quantity && (
                                    <Form.Control.Feedback type="invalid">
                                        {t(formErrors.quantity)}
                                    </Form.Control.Feedback>
                                )}
                                <Form.Text className="text-muted small">
                                    {t('quantity_help')}
                                </Form.Text>
                            </Form.Group>
                        </div>
                    </div>

                    <div className="row">
                        <div className="col-md-6">
                            <Form.Group className="mb-3">
                                <Form.Label>
                                    <span className="required-field">*</span> {t('event_date')}
                                </Form.Label>
                                <Form.Control
                                    type="date"
                                    isInvalid={validated && !!formErrors.eventDate}
                                    value={eventDate}
                                    onChange={(e) => setEventDate(e.target.value)}
                                />
                                {formErrors.eventDate && (
                                    <Form.Control.Feedback type="invalid">
                                        {t(formErrors.eventDate)}
                                    </Form.Control.Feedback>
                                )}
                                <Form.Text className="text-muted small">
                                    {t('event_date_help')}
                                </Form.Text>
                            </Form.Group>
                        </div>
                        <div className="col-md-6">
                            <Form.Group className="mb-3">
                                <Form.Label>
                                    <span className="required-field">*</span>{' '}
                                    {t('distribution_method')}
                                </Form.Label>
                                <Form.Control
                                    type="text"
                                    isInvalid={validated && !!formErrors.distributionMethod}
                                    value={distributionMethod}
                                    onChange={(e) => setDistributionMethod(e.target.value)}
                                />
                                {formErrors.distributionMethod && (
                                    <Form.Control.Feedback type="invalid">
                                        {t(formErrors.distributionMethod)}
                                    </Form.Control.Feedback>
                                )}
                                <Form.Text className="text-muted small">
                                    {t('distribution_method_help')}
                                </Form.Text>
                            </Form.Group>
                        </div>
                    </div>

                    <Form.Group className="mb-3">
                        <Form.Label>{t('notes')}</Form.Label>
                        <Form.Control
                            as="textarea"
                            rows={3}
                            value={notes}
                            onChange={(e) => setNotes(e.target.value)}
                        />
                        <Form.Text className="text-muted small">{t('notes_help')}</Form.Text>
                    </Form.Group>
                </Form>
            </Modal.Body>
            <Modal.Footer>
                <LoadingButton onClick={handleCreateSubmit} isLoading={isSubmitting}>
                    {t('create')}
                </LoadingButton>
                <Button variant="secondary" onClick={onHide}>
                    {t('cancel')}
                </Button>
            </Modal.Footer>
        </Modal>
    );
};

export default PromoItemModal;
